import { Vector3 } from "three";
import type { CenterlineData } from "./useTunnelCenterline";
import { storageKey } from "./projectConfig";

/**
 * A point in tunnel-local 3D coordinates.
 *
 * - station: distance along the tunnel centerline (meters)
 * - u: offset in the binormal direction (profile X / horizontal)
 * - v: offset in the -normal direction (profile Y / vertical up)
 *
 * This is the profile-plane coordinate system: (u, v) are the same as
 * the 2D profile (x, y) used in tunnelProfileShapes.ts.
 */
export interface TunnelPoint3D {
  station: number;
  u: number;
  v: number;
}

/**
 * A line drawn directly on the 3D tunnel surface.
 */
export type DrawToolType = "discontinuity" | "shotcrete" | "leakage";

export interface DrawToolConfig {
  color: string;
  lineWidth: number;
  tubeRadius: number;
  label: string;
  activeColor: string;
  /** If true, this tool draws closed polygons with hatch fill instead of lines */
  isPolygon?: boolean;
  /** Hatch pattern style for polygon tools */
  hatchStyle?: "diagonal" | "cross" | "dots";
  /** Fill opacity for polygon area */
  fillOpacity?: number;
}

export const DRAW_TOOL_CONFIG: Record<DrawToolType, DrawToolConfig> = {
  discontinuity: { color: "#ff4444", lineWidth: 2,  tubeRadius: 0.04, label: "Discontinuity", activeColor: "#9944cc" },
  shotcrete:     { color: "#888888", lineWidth: 3,  tubeRadius: 0.10, label: "Shotcrete",     activeColor: "#aaaaaa", isPolygon: true, hatchStyle: "diagonal", fillOpacity: 0.25 },
  leakage:       { color: "#4488ff", lineWidth: 3,  tubeRadius: 0.10, label: "Water Leak",    activeColor: "#4466ff", isPolygon: true, hatchStyle: "cross",    fillOpacity: 0.20 },
};

export interface Drawing3DLine {
  id: string;
  points: TunnelPoint3D[];
  color: string;
  lineWidth?: number;
  tubeRadius?: number;
  toolType?: DrawToolType;
  createdAt: number;
}

/**
 * A closed polygon drawn on the 3D tunnel surface, rendered as a hatched area.
 */
export interface Drawing3DPolygon {
  id: string;
  /** Vertices of the closed polygon in tunnel-local coords */
  vertices: TunnelPoint3D[];
  color: string;
  toolType: DrawToolType;
  hatchStyle: "diagonal" | "cross" | "dots";
  fillOpacity: number;
  createdAt: number;
  updatedAt?: number;
}

const LEGACY_STORAGE_KEY = "e16-tunnel:drawings-3d";
const LEGACY_POLYGON_STORAGE_KEY = "e16-tunnel:polygons-3d";

function drawingsKey(projectId: string): string {
  return storageKey(projectId, "drawings-3d");
}

function polygonsKey(projectId: string): string {
  return storageKey(projectId, "polygons-3d");
}

/** Migrate legacy (non-project-scoped) data to the project-scoped key if needed */
function migrateLegacy(legacyKey: string, projectKey: string): void {
  const existing = localStorage.getItem(projectKey);
  if (existing) return; // already have project-scoped data
  const legacy = localStorage.getItem(legacyKey);
  if (legacy) {
    localStorage.setItem(projectKey, legacy);
  }
}

export function loadDrawings3D(projectId: string): Drawing3DLine[] {
  try {
    const key = drawingsKey(projectId);
    migrateLegacy(LEGACY_STORAGE_KEY, key);
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw) as Drawing3DLine[];
  } catch {
    return [];
  }
}

export function saveDrawings3D(projectId: string, lines: Drawing3DLine[]): void {
  localStorage.setItem(drawingsKey(projectId), JSON.stringify(lines));
}

export function loadPolygons3D(projectId: string): Drawing3DPolygon[] {
  try {
    const key = polygonsKey(projectId);
    migrateLegacy(LEGACY_POLYGON_STORAGE_KEY, key);
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw) as Drawing3DPolygon[];
  } catch {
    return [];
  }
}

export function savePolygons3D(projectId: string, polygons: Drawing3DPolygon[]): void {
  localStorage.setItem(polygonsKey(projectId), JSON.stringify(polygons));
}

/**
 * Forward projection: tunnel-local (station, u, v) -> world 3D position.
 *
 * u maps to binormal direction, v maps to -normal direction
 * (same convention as TunnelProfiles.tsx).
 *
 * Interpolates between frame samples for smooth results.
 */
export function tunnelPoint3DToWorld(
  pt: TunnelPoint3D,
  data: CenterlineData,
  frames: { normals: Vector3[]; binormals: Vector3[] },
  curvePoints: Vector3[],
  frameSamples: number,
): Vector3 {
  const t = Math.max(0, Math.min(1, pt.station / data.totalLength));
  const continuous = t * frameSamples;
  const idxA = Math.min(Math.floor(continuous), frameSamples - 1);
  const idxB = Math.min(idxA + 1, frameSamples);
  const frac = continuous - idxA;

  // Interpolate center, normal, binormal
  const center = curvePoints[idxA].clone().lerp(curvePoints[idxB], frac);
  const normal = frames.normals[idxA].clone().lerp(frames.normals[idxB], frac).normalize();
  const binormal = frames.binormals[idxA].clone().lerp(frames.binormals[idxB], frac).normalize();

  return new Vector3(
    center.x + pt.u * binormal.x - pt.v * normal.x,
    center.y + pt.u * binormal.y - pt.v * normal.y,
    center.z + pt.u * binormal.z - pt.v * normal.z,
  );
}

/**
 * Reverse projection: world 3D position -> tunnel-local (station, u, v).
 *
 * 1. Find the nearest centerline segment.
 * 2. Project onto that segment to get an exact fractional index.
 * 3. Interpolate the Frenet frame at that position.
 * 4. Project the offset into the (binormal, -normal) plane.
 */
export function worldToTunnelPoint3D(
  worldPt: Vector3,
  data: CenterlineData,
  frames: { normals: Vector3[]; binormals: Vector3[] },
  curvePoints: Vector3[],
  frameSamples: number,
): TunnelPoint3D {
  // Find nearest curve point as starting point
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i <= frameSamples; i++) {
    const d = worldPt.distanceToSquared(curvePoints[i]);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }

  // Project onto the best neighboring segment to get exact fractional position.
  // Check segment [bestIdx-1, bestIdx] and [bestIdx, bestIdx+1], pick the
  // one that gives the closest projection.
  let bestT = bestIdx; // fractional index along curvePoints
  let bestProjDist = Infinity;

  const segments = [];
  if (bestIdx > 0) segments.push(bestIdx - 1);
  if (bestIdx < frameSamples) segments.push(bestIdx);

  for (const segStart of segments) {
    const a = curvePoints[segStart];
    const b = curvePoints[segStart + 1];
    const ab = b.clone().sub(a);
    const ap = worldPt.clone().sub(a);
    const abLenSq = ab.lengthSq();
    if (abLenSq < 1e-12) continue;

    // Parameter along segment [0, 1], clamped
    const param = Math.max(0, Math.min(1, ap.dot(ab) / abLenSq));
    const proj = a.clone().add(ab.multiplyScalar(param));
    const dist = worldPt.distanceToSquared(proj);

    if (dist < bestProjDist) {
      bestProjDist = dist;
      bestT = segStart + param;
    }
  }

  const station = (bestT / frameSamples) * data.totalLength;

  // Interpolate frame at the exact fractional position
  const idxA = Math.min(Math.floor(bestT), frameSamples - 1);
  const idxB = Math.min(idxA + 1, frameSamples);
  const frac = bestT - idxA;

  const center = curvePoints[idxA].clone().lerp(curvePoints[idxB], frac);
  const normal = frames.normals[idxA].clone().lerp(frames.normals[idxB], frac).normalize();
  const binormal = frames.binormals[idxA].clone().lerp(frames.binormals[idxB], frac).normalize();

  // Offset from centerline
  const offset = worldPt.clone().sub(center);

  // Project onto the local frame
  const u = offset.dot(binormal);
  const v = -offset.dot(normal); // negated because v maps to -normal

  return { station, u, v };
}
